import React from 'react';
import { Container, Typography, Box, Grid, Paper } from '@mui/material';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRoute, faHeadphones } from '@fortawesome/free-solid-svg-icons';

// Context
import { useI18n } from '../../context/I18nContext';

const SectionContainer = styled.section`
  padding: 5rem 0;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: var(--border-radius);
  margin: 2rem 0;
`;

const Feature = styled(Paper)`
  padding: 1.5rem;
  height: 100%;
  text-align: center;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  transition: var(--transition);
  
  &:hover {
    transform: translateY(-5px);
  }
  
  .icon {
    font-size: 2.5rem;
    color: var(--primary-color);
    margin-bottom: 1rem;
  }
  
  h3 {
    margin-bottom: 0.5rem;
    color: var(--primary-color);
  }
`;

const AboutSection: React.FC = () => {
  const { t } = useI18n();
  
  return (
    <SectionContainer id="about">
      <Container>
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h2" align="center" gutterBottom color="primary">
            {t('about_title')}
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 4, mx: -2 }}>
            <Box sx={{ width: { xs: '100%', md: '50%' }, px: 2 }}>
              <motion.div
                initial={{ x: -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
              >
                <Box>
                  <Typography variant="body1" paragraph>
                    {t('about_p1')}
                  </Typography>
                  
                  <Typography variant="body1" paragraph>
                    {t('about_p2')}
                  </Typography>
                  
                  <Typography variant="body1" paragraph>
                    {t('about_p3')}
                  </Typography>
                </Box>
              </motion.div>
            </Box>
            
            <Box sx={{ width: { xs: '100%', md: '50%' }, px: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1.5 }}>
                <Box sx={{ width: { xs: '100%', sm: '50%' }, px: 1.5 }}>
                  <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Feature elevation={3}>
                      <FontAwesomeIcon icon={faRoute} className="icon" />
                      <Typography variant="h5" component="h3">
                        {t('feature1_title')}
                      </Typography>
                      <Typography variant="body2">
                        {t('feature1_desc')}
                      </Typography>
                    </Feature>
                  </motion.div>
                </Box>
                
                <Box sx={{ width: { xs: '100%', sm: '50%' }, px: 1.5 }}>
                  <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                    viewport={{ once: true }}
                  >
                    <Feature elevation={3}>
                      <FontAwesomeIcon icon={faHeadphones} className="icon" />
                      <Typography variant="h5" component="h3">
                        {t('feature2_title')}
                      </Typography>
                      <Typography variant="body2">
                        {t('feature2_desc')}
                      </Typography>
                    </Feature>
                  </motion.div>
                </Box>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </SectionContainer>
  );
};

export default AboutSection;
